import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { flatMap } from 'rxjs/operators';
import { IncidentConfirmationService } from '../../driver/services/incident-confirmation/incident-confirmation.service';
import { getCurrentDateToString, getServerDate, isStartAfterEnd } from '../../shared/helpers/time-helpers.model';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerIndividualMedicalAlert } from '../models/customer-individual-medical-alert.model';
import { CustomerIndividualMedicalAlertType } from '../models/enums/customer-individual-medical-alert-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerModalBaseComponent } from '../shared/customer-modal-base.component';

export class MedicalAlertContext {
    constructor(
        public medicalAlertId: number,
        public customerNumberId: number
    ) { }
}

@Component({
    selector: 'customer-medical-modal',
    templateUrl: './customer-medical-modal.html'
})
export class MedicalAlertModalComponent extends CustomerModalBaseComponent {
    public medicalForm: UntypedFormGroup;
    public customerMedicalAlertTypes: any[];
    public showButtonLoading = false;
    public medicalAlertId: number;
    public maxDate: Date;

    private medicalAlert: CustomerIndividualMedicalAlert;
    private medicalAlertTypes = new CustomerIndividualMedicalAlertType();
    private customerNumberId: number;

    constructor(
        private location: PlatformLocation,
        private fb: UntypedFormBuilder,
        private customerManageService: CustomerManageService,
        private dialogRef: MatDialogRef<MedicalAlertModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) private data: MedicalAlertContext,
        public modalService: MvdModalService,
        private incidentConfirmationService: IncidentConfirmationService
    ) {
        super(modalService);
        this.customerNumberId = this.data.customerNumberId;
        this.medicalAlertId = this.data.medicalAlertId;
        this.medicalAlert = new CustomerIndividualMedicalAlert();
        this.maxDate = new Date();

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.medicalForm = this.fb.group({
            customerIndividualMedicalAlertTypeId: [null],
            effectiveDate: [null, Validators.required],
            endDate: [null]
        }, {
            validator: this.startDateValidator
        });

        this.customerMedicalAlertTypes = this.medicalAlertTypes.GetActive();
        if (this.medicalAlertId != 0) {
            this.initMedicalAlert();
        }
    }

    private initMedicalAlert(): void {
        this.customerManageService.getCustomerMedicalAlert(this.medicalAlertId)
            .subscribe(
                result => {
                    this.medicalAlert = result;
                    this.medicalForm.get('customerIndividualMedicalAlertTypeId').setValue(this.medicalAlert.customerIndividualMedicalAlertTypeId);
                    this.medicalForm.get('customerIndividualMedicalAlertTypeId').disable();
                    const effectiveDate = getServerDate(result, 'effectiveDate');
                    this.medicalForm.get('effectiveDate').setValue(effectiveDate);
                    const endDate = getServerDate(result, 'endDate');
                    this.medicalForm.get('endDate').setValue(endDate);
                },
                e => this.handleValidationResult(e)
            );
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public saveMedicalAlert(): void {
        if (this.medicalForm.valid) {
            this.showButtonLoading = true;
            const model = this.getModel();

            this.customerManageService.saveCustomerMedicalAlert(model)
                .subscribe(
                    _ => this.dialogRef.close(true),
                    e => this.incidentConfirmationService.confirmIncidentRedirect(null, e.error),
                    () => this.showButtonLoading = false);
        }
    }

    private startDateValidator(group: UntypedFormGroup): ValidationErrors | null {
        const effectiveDate = group.get('effectiveDate').value;
        const endDate = group.get('endDate').value;
        if (isStartAfterEnd(effectiveDate, endDate)) {
            return { 'startAfterEndDate': true };
        }
    }

    public deleteMedicalAlert(): void {
        const model = this.getModel();
        const itemType = this.medicalAlertTypes.FromId(model.customerIndividualMedicalAlertTypeId).displayValue;

        this.endDateRecordConfirmation(itemType)
            .pipe(
                flatMap(() => {
                    model.endDate = getCurrentDateToString();
                    return this.customerManageService.saveCustomerMedicalAlert(this.medicalAlert);
                })
            )
            .subscribe(
                () => {
                    this.showButtonLoading = false;
                    this.dialogRef.close(true);
                },
                e => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(e);
                });
    }

    private getModel(): CustomerIndividualMedicalAlert {
        const model = Object.assign(new CustomerIndividualMedicalAlert(), this.medicalAlert);

        model.customerIndividualMedicalAlertTypeId = this.medicalForm.get('customerIndividualMedicalAlertTypeId').value;
        model.effectiveDate = this.medicalForm.get('effectiveDate').value;
        model.endDate = this.medicalForm.get('endDate').value;

        model.customerNumberID = this.customerNumberId;

        return model;
    }
}
